import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Loader2 } from 'lucide-react';

// Pick the role-specific login URL based on the area the user was trying to reach.
// Falls back to the generic /login (still supported) for non-portal paths.
const loginPathFor = (pathname = '') => {
  if (pathname.startsWith('/admin')) return '/admin/login';
  if (pathname.startsWith('/telecaller')) return '/telecaller/login';
  if (pathname.startsWith('/member')) return '/member/login';
  return '/login';
};

export const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);

  // Timeout to prevent infinite loading
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setTimedOut(true);
      }, 10000); // 10 second timeout
      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading && !timedOut) {
    return (
      <div className="min-h-screen bg-[#0F0F10] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  // If timed out, clear token and redirect to the role-specific login
  if (timedOut) {
    localStorage.removeItem('bitz_token');
    return <Navigate to={loginPathFor(location.pathname)} state={{ from: location }} replace />;
  }

  if (!isAuthenticated) {
    return <Navigate to={loginPathFor(location.pathname)} state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    // Redirect based on role
    if (user?.role === 'super_admin' || user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'telecaller') {
      return <Navigate to="/telecaller" replace />;
    } else {
      return <Navigate to="/member" replace />;
    }
  }

  return children;
};

export const PublicRoute = ({ children }) => {
  const { user, loading, isAuthenticated } = useAuth();
  const [timedOut, setTimedOut] = useState(false);

  // Timeout to prevent infinite loading
  useEffect(() => {
    if (loading) {
      const timer = setTimeout(() => {
        setTimedOut(true);
        localStorage.removeItem('bitz_token');
      }, 10000); // 10 second timeout
      return () => clearTimeout(timer);
    }
  }, [loading]);

  if (loading && !timedOut) {
    return (
      <div className="min-h-screen bg-[#0F0F10] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  // If timed out, just show the login page
  if (timedOut) {
    return children;
  }

  if (isAuthenticated) {
    // Redirect based on role
    if (user?.role === 'super_admin' || user?.role === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user?.role === 'telecaller') {
      return <Navigate to="/telecaller" replace />;
    } else {
      return <Navigate to="/member" replace />;
    }
  }

  return children;
};
